import React, { useState, useEffect, useCallback } from 'react';
import '../shared.css';
import Sidebar from '../Components/Layout/sidebar';
import Topbar from '../Components/Layout/topbar';
import Button from '../Components/Comman/Button';
import Modal from '../Components/Comman/Modal';
import {
  FieldGroup, TextInput, SelectInput, FormGrid, FormActions, DeleteConfirm,
} from '../Components/Comman/FormFields';
import { supabase } from '../supabaseClient';

const STATUS_STYLE = {
  Active:   { bg: 'rgba(74,148,74,0.14)',  color: '#2d7a2d' },
  Paused:   { bg: 'rgba(204,153,0,0.14)', color: '#8a6600' },
  Inactive: { bg: 'rgba(42,42,42,0.1)',   color: '#555'    },
};

const STATUSES = ['Active', 'Paused', 'Inactive'];
const TRIGGERS = ['On page load', 'After 30s', 'After 60s', 'Exit intent', 'Scroll 60%', 'Scroll 80%'];
const blank = () => ({ title: '', trigger: 'On page load', status: 'Active', clicks: 0, impressions: 0 });

/* ── Row ── */
const PopupRow = ({ title, trigger, status, clicks, impressions, onEdit, onDelete }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Inactive;
  const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(1) : '0.0';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1fr',
      alignItems: 'center', padding: '14px 20px',
      background: 'rgba(245,240,232,0.7)', borderRadius: 8,
      border: '1px solid rgba(42,42,42,0.08)', gap: 12,
      transition: 'transform 0.15s, box-shadow 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(3px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <p style={{ fontFamily: '"Cormorant Garamond"', fontSize: 16, fontWeight: 600, color: '#191919' }}>{title}</p>
      <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#8B8B7A' }}>{trigger}</p>
      <span style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: s.bg, color: s.color, width: 'fit-content' }}>{status}</span>
      <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 500, color: '#191919' }}>{Number(impressions).toLocaleString()}</p>
      <p style={{ fontFamily: 'Inter', fontSize: 13, fontWeight: 500, color: '#191919' }}>{Number(clicks).toLocaleString()}</p>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#191919' }}>{ctr}%</span>
        <button
          type="button"
          onClick={onEdit}
          style={{ background: 'none', border: '0.762px solid rgba(42,42,42,0.2)', borderRadius: 5, padding: '3px 10px', fontFamily: 'Inter', fontSize: 11, cursor: 'pointer', color: '#191919', transition: 'background 0.15s', marginLeft: 4 }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(214,184,147,0.3)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >Edit</button>
        <button
          type="button"
          onClick={onDelete}
          style={{ background: 'rgba(180,60,60,0.07)', border: '0.762px solid rgba(180,60,60,0.2)', borderRadius: 5, padding: '3px 10px', fontFamily: 'Inter', fontSize: 11, cursor: 'pointer', color: '#a33', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(180,60,60,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(180,60,60,0.07)'}
        >Del</button>
      </div>
    </div>
  );
};

/* ── Form ── */
const PopupForm = ({ initial, onSave, onCancel, loading }) => {
  const [form, setForm] = useState(initial || blank());

  /* FIX: reset form when initial changes */
  useEffect(() => {
    setForm(initial || blank());
  }, [initial]);

  const set = (key, asNumber = false) => (val) =>
    setForm(f => ({ ...f, [key]: asNumber ? Number(val) : val }));

  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormGrid>
          <FieldGroup label="Popup Title" required>
            <TextInput value={form.title} onChange={set('title')} placeholder="e.g. Early Bird Offer" required />
          </FieldGroup>
          <FieldGroup label="Trigger" required>
            <SelectInput value={form.trigger} onChange={set('trigger')} options={TRIGGERS} required />
          </FieldGroup>
          <FieldGroup label="Status" required>
            <SelectInput value={form.status} onChange={set('status')} options={STATUSES} required />
          </FieldGroup>
          <FieldGroup label="Impressions">
            <TextInput value={form.impressions} onChange={set('impressions', true)} type="number" />
          </FieldGroup>
          <FieldGroup label="Clicks">
            <TextInput value={form.clicks} onChange={set('clicks', true)} type="number" />
          </FieldGroup>
        </FormGrid>
        <FormActions onCancel={onCancel} submitLabel={initial?.id ? 'Update Popup' : 'Create Popup'} loading={loading} />
      </div>
    </form>
  );
};

/* ── Page ── */
const PopupAds = () => {
  const [popups, setPopups]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [modal, setModal]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast]       = useState(null);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPopups = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('popup_ads').select('*').order('title');
    if (error) showToast('Failed to load: ' + error.message, true);
    else setPopups(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPopups(); }, [fetchPopups]);

  const handleCreate = async (form) => {
    setSaving(true);
    const { id, ...rest } = form;
    const { error } = await supabase.from('popup_ads').insert([rest]);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Popup created ✓');
    setModal(null);
    fetchPopups();
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    const { id, ...rest } = form;
    const { error } = await supabase.from('popup_ads').update(rest).eq('id', id);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Popup updated ✓');
    setModal(null);
    fetchPopups();
  };

  const handleDelete = async () => {
    setSaving(true);
    const { error } = await supabase.from('popup_ads').delete().eq('id', selected.id);
    setSaving(false);
    if (error) { showToast(error.message, true); return; }
    showToast('Popup deleted ✓');
    setModal(null);
    fetchPopups();
  };

  const closeModal = () => { setModal(null); setSelected(null); };

  const totalImpressions = popups.reduce((a, p) => a + Number(p.impressions || 0), 0);
  const totalClicks      = popups.reduce((a, p) => a + Number(p.clicks || 0), 0);
  const avgCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0.0';

  return (
    <main>
      <Sidebar />
      <div className='pagestrcutre'>
        <Topbar />

        {toast && (
          <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 99999, background: toast.isError ? '#a33' : '#2d7a2d', color: '#fff', padding: '12px 20px', borderRadius: 8, fontFamily: 'Inter', fontSize: 13, boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>
        )}

        <div className='pagetop_div'>
          <div className='pagetop_div1'>
            <h3 className='pagetop_text1'>Popup Ads</h3>
            {/* FIX: pass onClick to Button */}
            <Button
              text='+ Create Popup'
              icon={null}
              onClick={() => { setSelected(null); setModal('create'); }}
            />
          </div>
          <p className='pagetop_text2'>Manage promotional popups and track their performance</p>
        </div>

        <div className='page_content' style={{ paddingBottom: 30 }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1fr', padding: '10px 20px', gap: 12, marginBottom: 8 }}>
            {['Popup Name', 'Trigger', 'Status', 'Impressions', 'Clicks', 'CTR / Actions'].map(h => (
              <p key={h} style={{ fontFamily: 'Inter', fontSize: 11.5, fontWeight: 700, color: '#8B8B7A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</p>
            ))}
          </div>

          {loading ? (
            <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#8B8B7A', padding: 20 }}>Loading…</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {popups.map(p => (
                <PopupRow
                  key={p.id} {...p}
                  onEdit={() => { setSelected(p); setModal('edit'); }}
                  onDelete={() => { setSelected(p); setModal('delete'); }}
                />
              ))}
              {popups.length === 0 && (
                <p style={{ textAlign: 'center', padding: 40, fontFamily: '"Cormorant Garamond"', fontSize: 16, fontStyle: 'italic', color: 'rgba(13,13,13,0.4)' }}>No popups yet.</p>
              )}
            </div>
          )}

          {/* Summary */}
          <div style={{ marginTop: 24, display: 'flex', gap: 16 }}>
            {[
              { label: 'Total Impressions', value: totalImpressions.toLocaleString() },
              { label: 'Total Clicks',      value: totalClicks.toLocaleString()      },
              { label: 'Avg CTR',           value: `${avgCtr}%`                      },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, background: 'rgba(214,184,147,0.63)', borderRadius: 10, padding: '20px 24px',
                transition: 'transform 0.18s, box-shadow 0.18s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <p style={{ fontFamily: 'Inter', fontSize: 12, color: '#8B8B7A' }}>{s.label}</p>
                <p style={{ fontFamily: '"Cormorant Garamond"', fontSize: 28, fontWeight: 700, color: '#191919', marginTop: 4 }}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        <Modal open={modal === 'create'} onClose={closeModal} title="Create Popup Ad">
          <PopupForm onSave={handleCreate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'edit'} onClose={closeModal} title="Edit Popup Ad">
          <PopupForm initial={selected} onSave={handleUpdate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'delete'} onClose={closeModal} title="Delete Popup Ad" width={420}>
          <DeleteConfirm label={selected?.title} onConfirm={handleDelete} onCancel={closeModal} loading={saving} />
        </Modal>
      </div>
    </main>
  );
};

export default PopupAds;